import React, { useState, useEffect, useRef, useCallback } from "react";
import AsyncSelect from 'react-select/async';
import { useDispatch } from "react-redux";
import debounce from "lodash.debounce";
import * as actions from '../../../redux/actions/growthEngine';
import { toast, ToastContainer } from "react-toastify";

// react-select custom styles
const customStyles = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: 'white',
        border: '1px solid #D1D5DB', // same as border-gray-300
        borderRadius: '0.5rem',      // rounded-lg
        fontSize: '14px',
        padding: '0',
        minHeight: '44px',           // match input height (py-3)
        boxShadow: state.isFocused ? '0 0 0 2px #3B82F6' : 'none', // blue outline on focus
        '&:hover': {
            borderColor: '#9CA3AF', // hover border (gray-400)
        },
    }),
    input: (provided) => ({
        ...provided,
        color: '#111827', // text-gray-900
        fontSize: '14px',
        padding: '0',
        margin: '0',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#6B7280', // text-gray-500
        fontSize: '14px',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#F3F4F6' : 'white', // hover gray-100
        color: '#111827', // text-gray-900
        fontSize: '14px',
        cursor: 'pointer',
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '0.5rem',
        marginTop: '4px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    }),
};

export const EditGeographyModal = ({ campaignId, adAccountId, getAdSets, showEditGeographyModal, closeEditGeographyModal, budgetType, startTime, endTime,
    setRefreshFlag
}) => {
    const dispatch = useDispatch();
    const [isVisible, setIsVisible] = useState(showEditGeographyModal);
    const [showLoading, setShowLoading] = useState(false);

    // Initial normalized adSets (deep copy to avoid mutating parent)
    const getNormalizedAdSets = useCallback(() => [
        getAdSets?.[0]
            ? { ...getAdSets[0], location: { countries: [...getAdSets[0].location.countries], regions: [...getAdSets[0].location.regions], cities: [...getAdSets[0].location.cities] } }
            : { budgetPercentage: 100, location: { countries: [], regions: [], cities: [] } },

        getAdSets?.[1]
            ? { ...getAdSets[1], location: { countries: [...getAdSets[1].location.countries], regions: [...getAdSets[1].location.regions], cities: [...getAdSets[1].location.cities] } }
            : { budgetPercentage: 0, location: { countries: [], regions: [], cities: [] } },
    ], [getAdSets]);

    // Store initial recommended state in a ref
    const recommendedRef = useRef(getNormalizedAdSets());

    // State
    const [adSets, setAdSets] = useState(recommendedRef.current);
    const [percentages, setPercentages] = useState(recommendedRef.current.map(a => a.budgetPercentage || 0));
    const [locations, setLocations] = useState(recommendedRef.current.map(a => ({
        countries: a.location.countries,
        regions: a.location.regions,
        cities: a.location.cities
    })));

    // Smooth mount/unmount
    useEffect(() => {
        if (showEditGeographyModal) {
            setIsVisible(true);
            const normalized = getNormalizedAdSets();
            setAdSets(normalized);
            setPercentages(normalized.map(a => a.budgetPercentage || 0));
            setLocations(normalized.map(a => ({
                countries: a.location.countries,
                regions: a.location.regions,
                cities: a.location.cities
            })));
        } else {
            const timeout = setTimeout(() => setIsVisible(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [showEditGeographyModal, getNormalizedAdSets]);

    // Update budget ratio
    const handlePercentageChange = (index, value) => {
        const clamped = Math.max(0, Math.min(100, value));
        const otherIndex = index === 0 ? 1 : 0;
        const newPercentages = [...percentages];
        newPercentages[index] = clamped;
        newPercentages[otherIndex] = 100 - clamped;
        setPercentages(newPercentages);
    };

    // Remove location
    const handleRemove = (setIndex, type, loc) => {
        const updated = [...locations];
        updated[setIndex][type] = updated[setIndex][type].filter((l) => l.id !== loc.id);
        setLocations(updated);
    };

    const fetchLocations = debounce((inputValue, callback) => {
        if (inputValue.length > 2) {
            dispatch(actions.getTargetLocation({ query: inputValue }))
                .then((response) => {
                    if (response.length > 0) {
                        callback(response.map(item => ({
                            label: (
                                <div>
                                    <div>{formatLocationLabel(item)}</div>
                                    <div className="text-xs text-white-400">{item.type}</div>
                                </div>
                            ),
                            value: item.key, // Unique ID
                            name: item.name, // Only store name
                            type: item.type, // Type (city, region, country)
                            country_code: item.country_code,
                            region_id: item.region_id,
                            labelString: formatLocationLabel(item),
                        })));
                    } else {
                        callback([]);
                    }
                })
                .catch(() => callback([]));
        } else {
            callback([]);
        }
    }, 500);

    const formatLocationLabel = (location) => {
        if (location.type === "city") {
            return `${location.name}, ${location.region}, ${location.country_name}`;
        } else if (location.type === "region") {
            return `${location.name}, ${location.country_name}`;
        } else if (location.type === "country") {
            return `${location.name}`;
        }
        return location.name;  // Default fallback
    };

    const toastMsg = (msg, type) => {
        if (type === 'info') {
            toast.info(msg, {
                position: "top-center",
                theme: "colored",
                closeOnClick: true,
                autoClose: 1000,
                hideProgressBar: true,
            });
        } else if (type === 'error') {
            toast.error(msg, {
                position: "top-center",
                theme: "colored",
                closeOnClick: true,
                autoClose: 2000,
                hideProgressBar: true,
            });
        }
        else {
            toast.warn(msg, {
                position: "top-center",
                theme: "colored",
                closeOnClick: true,
                autoClose: 2000,
                hideProgressBar: true,
            });
        }

    };
    const handleLocationChange = (setIndex, selectedOption) => {
        const updatedLocations = [...locations];
        const typeKeyMap = {
            country: 'countries',
            region: 'regions',
            city: 'cities',
        };

        const key = typeKeyMap[selectedOption.type];

        if (!updatedLocations[setIndex][key]) {
            updatedLocations[setIndex][key] = [];
        }

        const formattedLocation = {
            id: selectedOption.value,
            name: selectedOption.name,
            country_code: selectedOption.country_code,
            region_id: selectedOption.region_id || null,
            labelString: selectedOption.labelString,
            type: selectedOption.type,
        };

        // Check for duplicates
        const isAlreadySelected = updatedLocations[setIndex][key].some(
            (loc) => loc.id === formattedLocation.id
        );
        if (isAlreadySelected) {
            toastMsg(`${formattedLocation.type} "${formattedLocation.name}" is already selected.`);
            return;
        }

        // Country selection conflict
        if (selectedOption.type === 'country') {
            if (
                updatedLocations[setIndex].cities.some(city => city.country_code === formattedLocation.id) ||
                updatedLocations[setIndex].regions.some(region => region.country_code === formattedLocation.id)
            ) {
                toastMsg(`Cannot select country "${formattedLocation.name}" because cities or regions from it are already selected.`);
                return;
            }
        }

        // Region selection conflict
        if (selectedOption.type === 'region') {
            if (
                updatedLocations[setIndex].countries.some(c => c.id === formattedLocation.country_code) ||
                updatedLocations[setIndex].cities.some(city => String(city.region_id) === String(formattedLocation.id))
            ) {
                toastMsg(`Cannot select region "${formattedLocation.name}" because its country or a city inside it is already selected.`);
                return;
            }
        }

        // City selection conflict
        if (selectedOption.type === 'city') {
            if (
                updatedLocations[setIndex].countries.some(c => c.id === formattedLocation.country_code) ||
                updatedLocations[setIndex].regions.some(r => String(r.id) === String(formattedLocation.region_id))
            ) {
                toastMsg(`Cannot select city "${formattedLocation.name}" because its country or region is already selected.`);
                return;
            }
        }

        updatedLocations[setIndex][key].push(formattedLocation);
        setLocations(updatedLocations);
    };

    const handleSave = async () => {
        const normalizedAdSets = [
            adSets[0] || { budgetPercentage: 100, location: { countries: [], regions: [], cities: [] } },
            adSets[1] || { budgetPercentage: 0, location: { countries: [], regions: [], cities: [] } },
        ];
        const adsetIds = normalizedAdSets.map(a => a._id);
        // Lifetime budget check: if campaign expired, do not allow save
        if (budgetType === "lifetime" && endTime) {
            const now = new Date();
            const campaignEnd = new Date(endTime);
            if (now > campaignEnd) {
                toastMsg("Cannot update ad sets: campaign has already expired.", "error");
                return; // stop execution
            }
        }

        // Validation: if both ad sets have locations but one has 0 budget
        const bothHaveLocations = normalizedAdSets.every((a, idx) =>
            locations[idx].countries.length + locations[idx].regions.length + locations[idx].cities.length > 0
        );

        const anyZeroBudget = percentages.some(p => p === 0);

        if (bothHaveLocations && anyZeroBudget) {
            toastMsg("Please assign a budget percentage for both ad sets before saving.", '')
            return; // stop execution
        }

        let data = {
            campaignId,
            adAccountId,
            adSetsUpdate: normalizedAdSets.map((a, idx) => ({
                adsetId: adsetIds[idx],
                budgetPercentage: percentages[idx],
                type: idx === 0 ? "primary" : "secondary",
                geo_locations: {
                    countries: locations[idx].countries,
                    regions: locations[idx].regions,
                    cities: locations[idx].cities,
                },
            })),
        };

        const currentComparable = data.adSetsUpdate.map(a => ({
            adsetId: a.adsetId,
            budgetPercentage: a.budgetPercentage,
            geo_locations: a.geo_locations,
        }));

        // Convert recommended into same shape
        const initialComparable = recommendedRef.current.map((a, idx) => ({
            adsetId: a._id,
            budgetPercentage: a.budgetPercentage,
            geo_locations: a.location,
        }));

        // Deep compare
        const hasChanges = JSON.stringify(currentComparable) !== JSON.stringify(initialComparable);

        if (!hasChanges) {
            toastMsg("No changes detected.", 'info');
            return;
        }
        await dispatch(actions.updateMetaAdset({ data, closeEditGeographyModal, setShowLoading, toast, setRefreshFlag }));
        // console.log(data, "hello data");
        // closeEditGeographyModal(); // only close if needed
    };

    // Reset handler
    const handleReset = () => {
        const recommended = recommendedRef.current.map(a => ({
            ...a,
            location: {
                countries: [...(a.location.countries || [])],
                regions: [...(a.location.regions || [])],
                cities: [...(a.location.cities || [])],
            }
        }));

        setAdSets(recommended);
        setPercentages(recommended.map(a => a.budgetPercentage || 0));
        setLocations(recommended.map(a => ({
            countries: a.location.countries,
            regions: a.location.regions,
            cities: a.location.cities
        })));
        toastMsg("Reset to original values", 'info')
    };
    if (!isVisible) return null; // unmount when hidden

    return (
        <div
            className={`fixed inset-0 flex items-center justify-center z-50 transition-colors duration-300 ${showEditGeographyModal ? "visible bg-black/50" : "invisible bg-transparent"
                }`}
            onClick={closeEditGeographyModal}
        >
            <ToastContainer />
            <div
                className={`bg-white rounded-xl shadow-xl max-w-[750px] w-full max-h-[96%] transform transition-all duration-300 overflow-auto mx-2 px-4 py-6 md:p-6 ${showEditGeographyModal ? "scale-100 opacity-100" : "scale-90 opacity-0"
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="heading-wrapper">
                        <h2 className="text-xl font-bold text-dark mb-[2px]">Edit Geography Targeting</h2>
                        <div className="text-base text-gray-600">Adjust the locations targeted in your campaign.</div>
                    </div>
                    <button
                        onClick={closeEditGeographyModal}
                        className="h-[35px] w-[35px] bg-gray-300 hover:bg-gray-400 text-black text-base rounded-full"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Fixed 2 slots */}
                {["Primary Market", "Secondary Market"].map((label, idx) => (
                    <section key={idx} className="geography-audience border rounded-md mb-4 p-4">
                        <div className="text-base font-bold text-dark mb-4">{label}</div>

                        {/* Name + Percentage Control */}
                        <div className="input-wrapper mb-4">
                            <label className="form-label">Audience Name</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="text"
                                    defaultValue={idx === 0 ? "Primary Market" : "Secondary Market"}
                                    readOnly
                                    className="flex-1 rounded-lg border border-gray-300 text-sm focus:outline-none px-3 py-3"
                                />

                                <button className="text-gray-600">
                                    <i className="bi bi-trash3"></i>
                                </button>

                                {/* Counter */}
                                <div className="bg-gray-200 rounded-full flex items-center gap-3 px-3 py-1">
                                    <button
                                        onClick={() => handlePercentageChange(idx, percentages[idx] - 1)}
                                        className="text-gray-600 text-lg"
                                    >
                                        <i className="bi bi-dash-circle"></i>
                                    </button>
                                    <span className="text-sm font-medium">{percentages[idx]}%</span>
                                    <button
                                        onClick={() => handlePercentageChange(idx, percentages[idx] + 1)}
                                        className="text-gray-600 text-lg"
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="mb-4">
                            <label className="form-label">Search for locations to add:</label>
                            <AsyncSelect
                                className="w-full"
                                styles={customStyles}
                                loadOptions={fetchLocations} // Fetch options asynchronously
                                defaultOptions // Show initial options
                                placeholder="Search for cities, states, and countries e.g. United States"
                                cacheOptions
                                noOptionsMessage={() => "No results found"} // Show message if no options
                                // onChange={handleLocationChange} // Handle selection
                                onChange={(selectedOption) => handleLocationChange(idx, selectedOption)}
                                controlShouldRenderValue={false}
                                components={{
                                    DropdownIndicator: () => null,
                                    IndicatorSeparator: () => null,
                                    SingleValue: ({ data }) => (
                                        <div>
                                            <div>{data.labelString}</div>
                                            <div className="text-xs text-white-400">{data.type}</div>
                                        </div>
                                    ),
                                }}
                            />
                            {/* </div> */}
                        </div>

                        {/* Locations */}
                        <div className="bg-theme_clr2 bg-opacity-10 rounded-lg p-3">
                            <div className="text-black text-base font-semibold mb-3">Targeted Locations</div>
                            {["countries", "regions", "cities"].map((type) => (
                                <div key={type} className="mb-3">
                                    <div className="text-sm font-medium text-gray-600 capitalize">{type}:</div>
                                    {locations[idx][type].length > 0 ? (
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            {locations[idx][type].map((loc) => (
                                                <span
                                                    key={loc.id}
                                                    className="flex items-center gap-1 border border-theme_clr2 text-theme_clr2 rounded-full px-3 py-1 text-sm"
                                                >
                                                    {loc.name}
                                                    <button
                                                        onClick={() => handleRemove(idx, type, loc)}
                                                        className="ml-1 text-theme_clr2 hover:text-theme_clr4"
                                                    >
                                                        <i className="bi bi-x text-lg"></i>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 italic">No {type}</div>
                                    )}
                                </div>
                            ))}

                            {/* {locations[idx]?.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {locations[idx].map((loc) => (
                                        <span
                                            key={loc?.id || loc?.name || loc}
                                            className="flex items-center gap-1 border border-theme_clr2 text-theme_clr2 rounded-full px-3 py-1 text-sm"
                                        >
                                            {loc?.name || loc?.id || loc}
                                            <button
                                                onClick={() => handleRemove(idx, loc)}
                                                className="ml-1 text-theme_clr2 hover:text-theme_clr4"
                                            >
                                                <i className="bi bi-x text-lg"></i>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-gray-400 italic">No locations for {label}</div>
                            )} */}
                        </div>
                    </section>
                ))}

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 mt-6">
                    <button className="text-base text-gray-500 hover:text-gray-700 font-medium"
                        onClick={handleReset}
                    >
                        Reset to Original
                    </button>
                    <button
                        onClick={handleSave}
                        className="bg-theme_clr2 text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-theme_clr4 tracking-[0.037] disabled:opacity-75 py-[10px] px-4 flex items-center gap-2"
                        // className="bg-theme_clr2 text-white text-[12.3px] font-inter font-medium tracking-[0.037] rounded-lg disabled:opacity-75 py-[10px] px-4 flex items-center gap-2"
                        disabled={showLoading}
                    >

                        {showLoading && <i className="bi bi-arrow-repeat animate-spin"></i>}
                        {showLoading ? 'Updating Ads' : 'Save & Close'}

                    </button>
                </div>
            </div>
        </div>

        // <div className={`fixed inset-0 flex items-center justify-center z-50 transition-colors duration-300 ${showEditGeographyModal ? "visible bg-black/50" : "invisible bg-transparent"}`} onClick={closeEditGeographyModal}>
        //     <div className={`bg-white rounded-xl shadow-xl max-w-[750px] w-full max-h-[96%] transform transition-all duration-300 overflow-auto mx-2 px-4 py-6 md:p-6 ${showEditGeographyModal ? "scale-100 opacity-100" : "scale-90 opacity-0"}`} onClick={(e) => e.stopPropagation()}>
        //         {/* Header */}
        //         <div className="flex items-start justify-between mb-4">
        //             <div className="heading-wrapper">
        //                 <h2 className="text-xl font-bold text-dark mb-[2px]">Edit Geography Targeting</h2>
        //                 <div className="text-base text-gray-600">Adjust the locations targeted in your campaign.</div>
        //             </div>
        //             <button onClick={closeEditGeographyModal} className="h-[35px] w-[35px] bg-gray-300 hover:bg-gray-400 text-black text-base rounded-full">
        //                 <i className="bi bi-x-lg"></i>
        //             </button>
        //         </div>

        //         <section className="geography-audience border rounded-md mb-4 p-4">
        //             <div className="text-base font-bold text-dark mb-4">Geography Audience #1</div>

        //             <div className="input-wrapper mb-4">
        //                 <label className="form-label">Audience Name</label>
        //                 <div className="flex items-center gap-3">
        //                     <input type="text" defaultValue="Touring Markets" className="flex-1 rounded-lg border border-gray-300 text-sm focus:outline-none px-3 py-3" />

        //                     {/* Delete button */}
        //                     <button className="text-gray-600">
        //                         <i className="bi bi-trash3"></i>
        //                     </button>

        //                     {/* Counter */}
        //                     <div className="bg-gray-200 rounded-full flex items-center gap-3 px-3 py-1">
        //                         <button onClick={decrease} className="text-gray-600 text-lg">
        //                             <i className="bi bi-dash-circle"></i>
        //                         </button>
        //                         <span className="text-sm font-medium">{percentage}%</span>
        //                         <button onClick={increase} className="text-gray-600 text-lg">
        //                             <i className="bi bi-plus-circle"></i>
        //                         </button>
        //                     </div>
        //                 </div>
        //             </div>

        //             <div className="mb-4">
        //                 <label className="form-label">Search for locations to add:</label>
        //                 <input type="text" placeholder="Search for cities, states, and countries e.g. United States" className="mt-1 w-full rounded-lg border border-gray-300 text-sm focus:outline-none px-3 py-3" />
        //             </div>

        //             <div className="bg-theme_clr2 bg-opacity-10 rounded-lg p-3">
        //                 <div className="text-black text-base font-semibold mb-3">Targeted Locations</div>
        //                 <div className="text-black text-base font-semibold mb-3">Countries:</div>
        //                 <div className="flex flex-wrap gap-2">
        //                     {locations.map((loc) => (
        //                         <span key={loc} className="flex items-center gap-1 border border-theme_clr2 text-theme_clr2 rounded-full px-3 py-1 text-sm">
        //                             {loc} (country)
        //                             <button onClick={() => handleRemove(loc)} className="ml-1 text-theme_clr2 hover:text-theme_clr4"><i className="bi bi-x text-lg"></i></button>
        //                         </span>
        //                     ))}
        //                 </div>
        //             </div>
        //         </section>

        //         <section className="geography-audience border rounded-md mb-4 p-4">
        //             <div className="text-base font-bold text-dark mb-4">Geography Audience #2</div>

        //             <div className="input-wrapper mb-4">
        //                 <label className="form-label">Audience Name</label>
        //                 <div className="flex items-center gap-3">
        //                     <input type="text" defaultValue="Global Markets" className="flex-1 rounded-lg border border-gray-300 text-sm focus:outline-none px-3 py-3" />

        //                     <button className="text-gray-600">
        //                         <i className="bi bi-trash3"></i>
        //                     </button>

        //                     <div className="bg-gray-200 rounded-full flex items-center gap-3 px-3 py-1">
        //                         <button onClick={decrease} className="text-gray-600 text-lg">
        //                             <i className="bi bi-dash-circle"></i>
        //                         </button>
        //                         <span className="text-sm font-medium">{percentage}%</span>
        //                         <button onClick={increase} className="text-gray-600 text-lg">
        //                             <i className="bi bi-plus-circle"></i>
        //                         </button>
        //                     </div>
        //                 </div>
        //             </div>

        //             <div className="mb-4">
        //                 <label className="form-label">Search for locations to add:</label>
        //                 <input type="text" placeholder="Search for cities, states, and countries e.g. United States" className="mt-1 w-full rounded-lg border border-gray-300 text-sm focus:outline-none px-3 py-3" />
        //             </div>

        //             <div className="bg-theme_clr2 bg-opacity-10 rounded-lg p-3">
        //                 <div className="text-black text-base font-semibold mb-3">Targeted Locations</div>
        //                 <div className="text-black text-base font-semibold mb-3">Countries:</div>
        //                 <div className="flex flex-wrap gap-2">
        //                     {locations.map((loc) => (
        //                         <span key={loc} className="flex items-center gap-1 border border-theme_clr2 text-theme_clr2 rounded-full px-3 py-1 text-sm">
        //                             {loc} (country)
        //                             <button onClick={() => handleRemove(loc)} className="ml-1 text-theme_clr2 hover:text-theme_clr4"><i className="bi bi-x text-lg"></i></button>
        //                         </span>
        //                     ))}
        //                 </div>
        //             </div>
        //         </section>

        //         {/* Footer */}
        //         <div className="flex items-center justify-end gap-3 mt-6">
        //             <button className="text-base text-gray-500 hover:text-gray-700 font-medium">Reset to Recommended</button>
        //             <button onClick={closeEditGeographyModal} className="bg-theme_clr2 text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-theme_clr4">Close</button>
        //         </div>
        //     </div>
        // </div>
    );
};
